import React from 'react';

import { Badge } from '../../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { Timestamp } from '../../../ui/timestamp';
import type { ScheduledJob } from '../../../../types/scheduledJobs';
import { formatCadence } from '../../../../types/scheduledJobs';
import { RunStatusIcon } from './statusIcon';
import ScheduledJobActions from './ScheduledJobActions';

interface ScheduledJobsListProps {
  jobs: ScheduledJob[];
  isLoading: boolean;
  onRowClick: (job: ScheduledJob) => void;
  onRunNow: (job: ScheduledJob) => void;
  onPause: (job: ScheduledJob) => void;
  onResume: (job: ScheduledJob) => void;
  onStop: (job: ScheduledJob) => void;
  onEdit: (job: ScheduledJob) => void;
  onDelete: (job: ScheduledJob) => void;
}

const JobStatusBadge: React.FC<{ job: ScheduledJob }> = ({ job }) => {
  if (job.status === 'paused') {
    return (
      <Badge variant="outline" className="border-amber-300 text-amber-700">
        Paused
      </Badge>
    );
  }
  if (job.latest_run?.status === 'running') {
    return (
      <Badge variant="secondary" className="gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
        </span>
        Running
      </Badge>
    );
  }
  return <Badge variant="default">Active</Badge>;
};

const ScheduledJobsList: React.FC<ScheduledJobsListProps> = ({
  jobs,
  isLoading,
  onRowClick,
  onRunNow,
  onPause,
  onResume,
  onStop,
  onEdit,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="space-y-3 p-6">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="h-10 animate-pulse rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-12 text-center">
        <p className="text-sm font-medium text-foreground">
          No scheduled jobs yet
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Create one to run a Juno agent on a recurring schedule.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Name</TableHead>
            <TableHead className="min-w-[260px]">Prompt</TableHead>
            <TableHead>Cadence</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last run</TableHead>
            <TableHead>Next run</TableHead>
            <TableHead className="w-[60px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => {
            const latestRun = job.latest_run;
            return (
              <TableRow
                key={job.id}
                className="cursor-pointer"
                onClick={() => onRowClick(job)}
                data-testid={`scheduled-job-row-${job.id}`}
              >
                <TableCell className="font-medium">
                  <span className="block max-w-[200px] truncate">{job.name}</span>
                </TableCell>
                <TableCell className="max-w-[320px] whitespace-normal align-top">
                  <p
                    className="text-[13px] leading-snug text-muted-foreground"
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                    }}
                    title={job.prompt}
                  >
                    {job.prompt}
                  </p>
                </TableCell>
                <TableCell className="text-[13px] text-muted-foreground">
                  {formatCadence(job.cadence, job.daily_time_utc)}
                </TableCell>
                <TableCell>
                  <JobStatusBadge job={job} />
                </TableCell>
                <TableCell className="text-[13px]">
                  {latestRun ? (
                    <div className="flex items-center gap-2">
                      <RunStatusIcon status={latestRun.status} />
                      <span className="text-muted-foreground">
                        <Timestamp
                          value={
                            latestRun.finished_at ?? latestRun.started_at ?? latestRun.created_at
                          }
                          format="relative"
                          fallback="Never"
                        />
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Never</span>
                  )}
                </TableCell>
                <TableCell className="text-[13px] text-muted-foreground">
                  {job.status === 'paused' ? (
                    'Paused'
                  ) : (
                    <Timestamp value={job.next_run_at} format="future" />
                  )}
                </TableCell>
                <TableCell
                  className="text-right"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ScheduledJobActions
                    job={job}
                    onRunNow={onRunNow}
                    onPause={onPause}
                    onResume={onResume}
                    onStop={onStop}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ScheduledJobsList;
